import { reFetchCurrentAccountBasedOnSelectedOrg } from 'modules/access/reducers/account';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { trackOrganizationSwitch } from '../../tracking/utils/tracking';

import { createReducer } from 'utils/redux';
import { getCookie, setCookie } from 'utils/cookie';
import { sortOldestFirst } from 'utils/list';
import { API, get, post, put } from 'utils/api';
import { logError } from 'utils/log';

import type { Organization, Organizations, ThunkAction } from 'types';

const RECEIVE_ORGANIZATIONS = 'organizations/RECEIVE_ORGANIZATIONS';
const ORGANIZATION_SELECTED = 'organizations/ORGANIZATION_SELECTED';
const ACTIVE_ORGANIZATION_UPDATED = 'organizations/ACTIVE_ORGANIZATION_UPDATED';
const ORGANIZATION_COOKIE = 'org';

const { GET_CURRENT_USER } = API.END_POINTS.API;
const { ORGANIZATIONS, USERS } = API.END_POINTS;

const initialState: Organizations = {
  currentOrganization: null,
  currentOrganizationId: null,
  list: [],
  mapByOrganizationId: {},
  activeOrganizationUpdated: false
};

function getCurrentOrganization(organizations) {
  if (!organizations.length) {
    return null;
  }

  const previousOrganizationId = Number(getCookie(ORGANIZATION_COOKIE));
  const firstOrganization = organizations[0];

  if (isNaN(previousOrganizationId)) { // eslint-disable-line no-restricted-globals
    return firstOrganization;
  }

  const currentOrganization = organizations
    .find((organization) => organization.id === previousOrganizationId);

  return currentOrganization || firstOrganization;
}

function organizationSelected(organization) {
  return {
    type: ORGANIZATION_SELECTED,
    payload: {
      organization
    }
  };
}

function activeOrganizationUpdated() {
  return {
    type: ACTIVE_ORGANIZATION_UPDATED
  };
}

export function userActiveOrganizationChanged(organization?: Organization | null): ThunkAction {
  return (dispatch, getState) => {
    const currentAccount = getState().account.currentAccount;
    const activeOrgUpdated = getState().organizations.activeOrganizationUpdated;
    const currentOrganizationId = getState().organizations.currentOrganizationId;

    if (!organization || !organization.id || !currentAccount || (activeOrgUpdated && organization.id === currentOrganizationId)) {
      return;
    }

    const orgId = organization.id;
    const userId = currentAccount.id;
    const updateActiveOrgUrl = USERS.updateActiveOrganization(userId);
    const trackRecentUrl = USERS.trackActivity(userId);

    const activeOrgData = { id: orgId };
    const orgSwitchTrackingData = { type: 'organization_switch', resource_id: orgId, user_id: userId };

    put(updateActiveOrgUrl, { data: activeOrgData }, { suppressGlobalNotification: true })
      .then(() => dispatch(reFetchCurrentAccountBasedOnSelectedOrg()))
      .catch((error) => {
        logError(error);
      });

    post(trackRecentUrl, { data: orgSwitchTrackingData }, { suppressGlobalNotification: true })
      .catch((error) => {
        logError(error);
      });
  };
}

export function selectOrganization(organization) {
  return (dispatch, getState) => {
    const { currentAccount } = getState().account;

    if (organization && organization.id) {
      setCookie(ORGANIZATION_COOKIE, organization.id);
    }
    dispatch(userActiveOrganizationChanged(organization));
    dispatch(organizationSelected(organization));
    dispatch(activeOrganizationUpdated());
    trackOrganizationSwitch(currentAccount, organization);
  };
}

export function selectOrganizationById(organizationId) {
  return (dispatch, getState) => {
    const organization = getState().organizations.mapByOrganizationId[organizationId];
    if (!organization) {
      return Promise.resolve(null);
    }

    return dispatch(selectOrganization(organization));
  };
}

function receiveOrganizations(organizations: Array<Organization> = []) {
  return {
    type: RECEIVE_ORGANIZATIONS,
    payload: {
      organizations
    }
  };
}

export function organizationsListUpdated(data: Array<Organization> = []) {
  return (dispatch) => {
    const sortedOrganizations = sortOldestFirst(data) as Array<Organization>;
    dispatch(receiveOrganizations(sortedOrganizations));
    const organization = getCurrentOrganization(sortedOrganizations);
    dispatch(selectOrganization(organization));
  };
}

export function fetchCurrentUserOrganizations() {
  const url = GET_CURRENT_USER;
  const loaderId = `organizations:${url}`; // to don't mix with currentAccount request

  return (dispatch) => {
    dispatch(showLoading(loaderId));

    return get(url)
      .then((response) => {
        const account = response.data;
        dispatch(organizationsListUpdated(account.organizations));
        dispatch(hideLoading(loaderId));
        return account.organizations;
      })
      .catch(() => dispatch(hideLoading(loaderId)));
  };
}

export function searchOrganization(resource, resourceId) {
  const url = ORGANIZATIONS.searchOrganization(resource, resourceId);

  return () => get(url)
    .then((response: { data: Organization }) => response.data)
    .catch(() => null);
}

export default createReducer<Organizations, any>('organizations', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RECEIVE_ORGANIZATIONS]: (state, { organizations }) => {
    const mapByOrganizationId = {};

    organizations.forEach((organization) => {
      mapByOrganizationId[organization.id] = organization;
    });

    return {
      ...state,
      mapByOrganizationId,
      list: organizations
    };
  },
  [ORGANIZATION_SELECTED]: (state, { organization }) => ({
    ...state,
    currentOrganization: organization,
    currentOrganizationId: organization ? organization.id : null
  }),
  [ACTIVE_ORGANIZATION_UPDATED]: (state) => ({
    ...state,
    activeOrganizationUpdated: true
  })
});
